import React from "react";
import { useParams } from "react-router-dom";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { Send } from "lucide-react";
import dayjs from "dayjs";
import isEmpty from "lodash.isempty";
import isNil from "lodash.isnil";
import { GetBattleByIdApiResponse as Battle, Comment } from "@dripit/types";

import { Button, Input, useToast } from "@/components/ui";
import { useTranslate } from "@/hooks";
import { postBattleComment } from "@/api/battleQueries";
import { CommentItem, NestedComment } from "./CommentItem";
import { cn } from "@/helpers/tailwind";

interface CommentTextAreaProps {
  replyTo?: Comment["id"];
}

interface CommentsTabProps {
  battle?: Battle;
  refetchBattle?: () => void;
}

export const CommentsTab: React.FC<CommentsTabProps> = ({
  battle,
  refetchBattle,
}) => {
  const t = useTranslate();
  const isLoggedIn = useIsLoggedIn();
  const { comments, battleSides } = battle ?? {};
  const { id } = useParams<{ id: string }>();
  const mutation = postBattleComment(id ?? "");
  const disableCommenting = React.useMemo(
    () => !isLoggedIn || dayjs().isAfter(dayjs((battle?.end_date ?? 0) * 1000)),
    [battle, isLoggedIn]
  );
  const nestedComments = React.useMemo(
    () =>
      comments
        ?.sort((a, b) => a.created_at - b.created_at)
        ?.reduce((list, comment) => {
          if (isNil(comment.replies_to)) {
            return [...list, comment];
          }
          return list.map((c) =>
            c.id !== comment.replies_to
              ? c
              : { ...c, replies: [...(c.replies ?? []), comment] }
          );
        }, [] as NestedComment[]) ?? [],
    [comments]
  );

  const CommentTextArea: React.FC<CommentTextAreaProps> = (props) => {
    const { toast } = useToast();
    const [commentInput, setCommentInput] = React.useState("");

    const handlePostComment = async (
      commentInput: string,
      replyTo?: Comment["id"]
    ) => {
      try {
        await mutation.mutateAsync(
          { comment: commentInput, replies_to: replyTo },
          {
            onSuccess: async () => {
              refetchBattle?.();
            },
          }
        );
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description:
            "Something went wrong while posting a comment. Please try again later.",
          variant: "destructive",
        });
      }
      setCommentInput("");
    };

    return (
      <div className="mb-4 flex w-full justify-between gap-2 rounded-lg border border-slate-500 p-2">
        <Input
          className="h-auto flex-grow border-none py-0"
          value={commentInput}
          placeholder={t("components.add_comment")}
          onChange={(event) => setCommentInput(event.target.value)}
        />
        <Button
          disabled={isEmpty(commentInput.trim())}
          size="sm"
          className={cn(
            "!h-7 bg-indigo-600 text-white hover:bg-indigo-600/80",
            "disabled:bg-slate-600 disabled:text-slate-300"
          )}
          onClick={() => handlePostComment(commentInput, props?.replyTo)}>
          <Send /> {mutation.isPending ? "Sending..." : t("components.send")}
        </Button>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {!disableCommenting && <CommentTextArea />}

      {/* comment list */}
      <div className="flex flex-col gap-5">
        {nestedComments.map((comment, index) => (
          <CommentItem
            key={index}
            battleSides={battleSides ?? []}
            comment={comment}
            replyTextArea={
              disableCommenting ? null : (
                <CommentTextArea replyTo={comment.id} />
              )
            }
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(CommentsTab);
